"use client";

import { c as _c } from "react/compiler-runtime";
import { Edit2, MoreVerticalIcon, Phone, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SidebarMenuAction, SidebarMenuButton } from "@/components/ui/sidebar";
import { useDeleteRealtimeHistory, useUpdateRealtimeHistory } from "@/hooks/usePlaygroundHistory";
import { formatCallDuration } from "@/lib/call-history";
import { HistorySkeleton } from "./history-skeleton";
export interface CallHistoryItem {
  id: string;
  title: string;
  createdAt: string;
  model: string;
  voice: string | null;
  durationSeconds: number;
  turnCount: number;
}
interface CallHistoryListProps {
  items: CallHistoryItem[];
  isLoading?: boolean;
  currentItemId?: string | null;
  onItemClick: (itemId: string) => void;
  onItemDeleted?: (itemId: string) => void;
}
type CallHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "item";
  key: string;
  item: CallHistoryItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_ITEM = 60;
interface CallHistoryRowProps {
  rows: CallHistoryRow[];
  currentItemId?: string | null;
  editingId: string | null;
  editTitle: string;
  pendingFocusId: string | null;
  onItemClick: (itemId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveEdit: (id: string, original: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (id: string) => void;
  onStartEdit: (id: string, title: string) => void;
  onEditFocused: () => void;
}
function getCallHistoryRowHeight(index: number, {
  rows
}: CallHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_ITEM;
}
function EditCallTitleInput(t0) {
  const $ = _c(17);
  const {
    itemId,
    value,
    original,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  const canceledRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== itemId || $[7] !== onSave || $[8] !== original) {
    t4 = () => {
      if (!canceledRef.current) {
        onSave(itemId, original);
      }
      canceledRef.current = false;
    };
    $[6] = itemId;
    $[7] = onSave;
    $[8] = original;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        canceledRef.current = true;
        onCancel();
      }
    };
    $[10] = onCancel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function CallHistoryRowComponent(t0) {
  const $ = _c(35);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentItemId,
    editingId,
    editTitle,
    pendingFocusId,
    onItemClick,
    onEditTitleChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteItem,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    item
  } = row;
  const isEditing = editingId === item.id;
  const isActive = currentItemId === item.id;
  let t1;
  if ($[8] !== editTitle || $[9] !== isActive || $[10] !== isEditing || $[11] !== item.createdAt || $[12] !== item.durationSeconds || $[13] !== item.id || $[14] !== item.title || $[15] !== onCancelEdit || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onItemClick || $[19] !== onSaveEdit || $[20] !== pendingFocusId) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditCallTitleInput itemId={item.id} value={editTitle} original={item.title} shouldFocus={pendingFocusId === item.id} onChange={onEditTitleChange} onSave={onSaveEdit} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton onClick={() => onItemClick(item.id)} isActive={isActive} className="h-full! w-full justify-start group relative pr-2 !transition-none group-hover/call-row:pr-9" type="button"><div className="flex items-start gap-2 min-w-0 w-full"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border mt-0.5"><Phone className="h-4 w-4 text-muted-foreground" /></div><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{item.title}</div><div className="text-xs text-muted-foreground">{formatCallDuration(item.durationSeconds)} ·{" "}{new Date(item.createdAt).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit"
            })}</div></div></div></SidebarMenuButton>;
    $[8] = editTitle;
    $[9] = isActive;
    $[10] = isEditing;
    $[11] = item.createdAt;
    $[12] = item.durationSeconds;
    $[13] = item.id;
    $[14] = item.title;
    $[15] = onCancelEdit;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onItemClick;
    $[19] = onSaveEdit;
    $[20] = pendingFocusId;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  let t2;
  if ($[22] !== isEditing || $[23] !== item.id || $[24] !== item.title || $[25] !== onDeleteItem || $[26] !== onStartEdit) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" onClick={_temp} className="pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/call-row:flex group-hover/call-row:pointer-events-auto group-hover/call-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-40"><DropdownMenuItem onClick={e_0 => {
            e_0.stopPropagation();
            onStartEdit(item.id, item.title);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onDeleteItem(item.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[22] = isEditing;
    $[23] = item.id;
    $[24] = item.title;
    $[25] = onDeleteItem;
    $[26] = onStartEdit;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  let t3;
  if ($[28] !== t1 || $[29] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/call-row relative h-full">{t1}{t2}</div></div>;
    $[28] = t1;
    $[29] = t2;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== ariaAttributes || $[32] !== style || $[33] !== t3) {
    t4 = <div {...ariaAttributes} style={style}>{t3}</div>;
    $[31] = ariaAttributes;
    $[32] = style;
    $[33] = t3;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  return t4;
}
function _temp(e) {
  e.stopPropagation();
}
function groupItemsByDate(items: CallHistoryItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as CallHistoryItem[],
    yesterday: [] as CallHistoryItem[],
    lastWeek: [] as CallHistoryItem[],
    older: [] as CallHistoryItem[]
  };
  items.forEach(item => {
    const itemDate = new Date(item.createdAt);
    if (itemDate.toDateString() === today.toDateString()) {
      groups.today.push(item);
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(item);
    } else if (itemDate >= lastWeek) {
      groups.lastWeek.push(item);
    } else {
      groups.older.push(item);
    }
  });
  return groups;
}

/**
 * Date-grouped list of past voice calls for the realtime sidebar, with inline
 * rename and delete. Mirrors the other studio history lists.
 */
export function CallHistoryList(t0) {
  const $ = _c(31);
  const {
    items,
    isLoading: t1,
    currentItemId,
    onItemClick,
    onItemDeleted
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const renameItem = useUpdateRealtimeHistory();
  const deleteItem = useDeleteRealtimeHistory();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [pendingFocusId, setPendingFocusId] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (id, title) => {
      setEditingId(id);
      setEditTitle(title);
      setPendingFocusId(id);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const startEdit = t2;
  let t3;
  if ($[1] !== editTitle || $[2] !== renameItem) {
    t3 = (id_0, original) => {
      const next = editTitle.trim();
      if (next && next !== original) {
        renameItem.mutate({
          params: {
            path: {
              id: id_0
            }
          },
          body: {
            title: next
          }
        });
      }
      setEditingId(null);
      setEditTitle("");
      setPendingFocusId(null);
    };
    $[1] = editTitle;
    $[2] = renameItem;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const saveEdit = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setEditingId(null);
      setEditTitle("");
      setPendingFocusId(null);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const cancelEdit = t4;
  let t5;
  if ($[5] !== deleteItem || $[6] !== onItemDeleted) {
    t5 = id_1 => {
      deleteItem.mutate({
        params: {
          path: {
            id: id_1
          }
        }
      }, {
        onSuccess: () => onItemDeleted?.(id_1)
      });
    };
    $[5] = deleteItem;
    $[6] = onItemDeleted;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleDeleteItem = t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setPendingFocusId(null);
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const onEditFocused = t6;
  let t7;
  if ($[9] !== items) {
    t7 = groupItemsByDate(items);
    $[9] = items;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const itemGroups = t7;
  let rows;
  if ($[11] !== itemGroups.lastWeek || $[12] !== itemGroups.older || $[13] !== itemGroups.today || $[14] !== itemGroups.yesterday) {
    const groups = [{
      title: "Today",
      items: itemGroups.today
    }, {
      title: "Yesterday",
      items: itemGroups.yesterday
    }, {
      title: "Last 7 days",
      items: itemGroups.lastWeek
    }, {
      title: "Older",
      items: itemGroups.older
    }];
    rows = [];
    groups.forEach((t8, groupIndex) => {
      const {
        title: title_0,
        items: groupedItems
      } = t8;
      if (groupedItems.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title_0}`,
        title: title_0
      });
      groupedItems.forEach(item => {
        rows.push({
          type: "item",
          key: `item-${item.id}`,
          item
        });
      });
      const hasNextGroupWithItems = groups.slice(groupIndex + 1).some(_temp2);
      if (hasNextGroupWithItems) {
        rows.push({
          type: "spacer",
          key: `spacer-${title_0}`
        });
      }
    });
    $[11] = itemGroups.lastWeek;
    $[12] = itemGroups.older;
    $[13] = itemGroups.today;
    $[14] = itemGroups.yesterday;
    $[15] = rows;
  } else {
    rows = $[15];
  }
  const historyRows = rows;
  let t8;
  if ($[16] !== currentItemId || $[17] !== editTitle || $[18] !== editingId || $[19] !== handleDeleteItem || $[20] !== historyRows || $[21] !== onItemClick || $[22] !== pendingFocusId || $[23] !== saveEdit) {
    t8 = {
      rows: historyRows,
      currentItemId,
      editingId,
      editTitle,
      pendingFocusId,
      onItemClick,
      onEditTitleChange: setEditTitle,
      onSaveEdit: saveEdit,
      onCancelEdit: cancelEdit,
      onDeleteItem: handleDeleteItem,
      onStartEdit: startEdit,
      onEditFocused
    };
    $[16] = currentItemId;
    $[17] = editTitle;
    $[18] = editingId;
    $[19] = handleDeleteItem;
    $[20] = historyRows;
    $[21] = onItemClick;
    $[22] = pendingFocusId;
    $[23] = saveEdit;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const rowProps = t8;
  if (isLoading && items.length === 0) {
    let t9;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <HistorySkeleton withThumbnail={true} />;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    return t9;
  }
  if (items.length === 0) {
    let t9;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="flex flex-col items-center justify-center py-8 text-center"><Phone className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No call history</p><p className="text-xs text-muted-foreground">Finish a call to see its transcript here</p></div>;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    return t9;
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      width: "100%"
    };
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== historyRows.length || $[29] !== rowProps) {
    t10 = <List className="min-h-0 w-full flex-1" style={t9} rowComponent={CallHistoryRowComponent} rowCount={historyRows.length} rowHeight={getCallHistoryRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[28] = historyRows.length;
    $[29] = rowProps;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp2(group) {
  return group.items.length > 0;
}